import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/series" },
  { label: "综艺", to: "/category/variety" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?query=${encodeURIComponent(value)}` : "/search");
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
      isActive ? "text-secondary-600" : "text-foreground-700 hover:text-primary-600",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
            <i className="ri-play-fill text-lg"></i>
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
            WWW.<span className="text-accent-500">17C</span>
          </span>
        </Link>

        {/* 桌面导航 */}
        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* 桌面搜索 */}
        <form onSubmit={submitSearch} className="hidden md:block">
          <div className="flex h-9 w-56 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 focus-within:border-secondary-400 lg:w-64">
            <i className="ri-search-line text-sm text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视 / 资讯"
              className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </form>

        {/* 移动端汉堡按钮 */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开导航菜单"
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 hover:bg-background-200 md:hidden"
        >
          <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </div>

      {/* 移动端展开面板 */}
      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="flex h-10 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 focus-within:border-secondary-400">
              <i className="ri-search-line text-sm text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视 / 资讯"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                autoFocus
              />
            </div>
          </form>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "rounded-md px-3 py-2.5 text-sm font-medium",
                    isActive ? "bg-secondary-100 text-secondary-700" : "text-foreground-700",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}